(() => {
  if (window.AdelaisVariantGalleryLoaded) return;
  window.AdelaisVariantGalleryLoaded = true;

  const normalize = (value) =>
    String(value || '')
      .trim()
      .toLowerCase()
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

  const getImageMarker = (slide) => {
    const image = slide.querySelector('[data-variant]');
    const value = image?.getAttribute('data-variant') || '';
    const match = value.match(/variant--([a-z0-9-]+)/i);

    return match ? normalize(match[1]) : '';
  };

  const getProductData = (section) => {
    const script = section.querySelector('[data-product-json]');

    if (!script) return null;

    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.warn('Adelais variant gallery: invalid product JSON.', error);
      return null;
    }
  };

  const getColorOptionIndex = (product) => {
    return (product.options || []).findIndex((option) => {
      const name = typeof option === 'string' ? option : option.name;
      return /^(color|colour)$/i.test(String(name || '').trim());
    });
  };

  const getInitialVariant = (section, product) => {
    const selectedId =
      section.querySelector('[data-product-select]')?.value ||
      new URL(window.location.href).searchParams.get('variant') ||
      section.dataset.acInitialVariantId;

    return (
      product.variants.find(
        (variant) => String(variant.id) === String(selectedId)
      ) || product.variants[0]
    );
  };

  const getVisibleSlides = (images) => {
    return Array.from(
      images.querySelectorAll('.product__slide[data-media-id]')
    ).filter((slide) => !slide.hidden);
  };

  const selectSlide = (images, slide) => {
    if (!slide) return;

    images.dispatchEvent(
      new CustomEvent('theme:media:select', {
        detail: {
          id: slide.dataset.mediaId
        }
      })
    );
  };

  const setElementVisibility = (element, show) => {
    if (!element) return;

    element.hidden = !show;

    if (show) {
      element.style.removeProperty('display');
    } else {
      element.style.setProperty('display', 'none', 'important');
    }
  };

  const applyVariant = (section, product, colorIndex, variant) => {
    if (!variant) return;

    const selectedColor = normalize(variant.options[colorIndex]);
    const images = section.querySelector('product-images');

    if (!images || !selectedColor) return;

    const slides = Array.from(
      images.querySelectorAll('.product__slide[data-media-id]')
    );

    slides.forEach((slide) => {
      const imageMarker = getImageMarker(slide);

      /*
       * Images without a variant marker are shared and remain visible.
       */
      const show = !imageMarker || imageMarker === selectedColor;

      setElementVisibility(slide, show);

      const thumbnailLink = section.querySelector(
        `.product__thumb [data-media-id="${slide.dataset.mediaId}"]`
      );

      setElementVisibility(
        thumbnailLink?.closest('.product__thumb'),
        show
      );
    });

    const visibleSlides = getVisibleSlides(images);
    const featuredMediaId = variant.featured_media?.id;

    const preferredSlide =
      visibleSlides.find(
        (slide) =>
          String(slide.dataset.imageId) === String(featuredMediaId)
      ) || visibleSlides[0];

    requestAnimationFrame(() => {
      selectSlide(images, preferredSlide);
    });
  };

  const initializeGallery = (section) => {
    if (
      !section ||
      section.dataset.acVariantGalleryReady === 'true'
    ) {
      return;
    }

    const product = getProductData(section);
    const images = section.querySelector('product-images');

    if (!product || !images) return;

    const colorIndex = getColorOptionIndex(product);

    const hasMarkedImages = Array.from(
      images.querySelectorAll('.product__slide[data-media-id]')
    ).some((slide) => getImageMarker(slide));

    if (colorIndex < 0 || !hasMarkedImages) return;

    section.dataset.acVariantGalleryReady = 'true';

    let currentColor = normalize(
      getInitialVariant(section, product)?.options[colorIndex]
    );

    section.addEventListener('theme:variant:change', (event) => {
      const variant = event.detail?.variant;
      if (!variant) return;

      const nextColor = normalize(variant.options[colorIndex]);
      const colorChanged = nextColor && nextColor !== currentColor;
      currentColor = nextColor;

      applyVariant(section, product, colorIndex, variant);

      if (
        !colorChanged ||
        !window.matchMedia('(max-width: 749px)').matches ||
        document.querySelector('adelais-mobile-purchase dialog[open]')
      ) return;

      requestAnimationFrame(() => {
        const headerHeight =
          document.querySelector('[data-header-height]')?.getBoundingClientRect().height || 0;
        const top = window.scrollY + images.getBoundingClientRect().top - headerHeight - 8;

        if (window.scrollY > top + 8) {
          window.scrollTo({
            top: Math.max(0, top),
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
              ? 'auto'
              : 'smooth'
          });
        }
      });
    });

    /*
     * Preserve the theme's native arrows, keyboard navigation and
     * mobile swipe. Only skip images hidden by the color filter.
     */
    images.getNextMediaId = () => {
      const visibleSlides = getVisibleSlides(images);

      if (!visibleSlides.length) return null;

      const activeSlide = images.querySelector(
        '.product__slide.media--active:not([hidden])'
      );

      let currentIndex = visibleSlides.indexOf(activeSlide);

      if (currentIndex < 0) {
        currentIndex = 0;
      }

      const nextIndex = (currentIndex + 1) % visibleSlides.length;

      return visibleSlides[nextIndex].dataset.mediaId;
    };

    images.getPreviousMediaId = () => {
      const visibleSlides = getVisibleSlides(images);

      if (!visibleSlides.length) return null;

      const activeSlide = images.querySelector(
        '.product__slide.media--active:not([hidden])'
      );

      let currentIndex = visibleSlides.indexOf(activeSlide);

      if (currentIndex < 0) {
        currentIndex = 0;
      }

      const previousIndex =
        (currentIndex - 1 + visibleSlides.length) %
        visibleSlides.length;

      return visibleSlides[previousIndex].dataset.mediaId;
    };

    applyVariant(
      section,
      product,
      colorIndex,
      getInitialVariant(section, product)
    );
  };

  const initializeAll = (root = document) => {
    root
      .querySelectorAll('[data-ac-variant-gallery]')
      .forEach(initializeGallery);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initializeAll();
    });
  } else {
    initializeAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initializeAll(event.target);
  });
})();